/**
 * Club member payment / bank-statement search aliases (host/admin only via RLS).
 */
import { supabase } from '../utils/supabase';

const MAX_ALIASES = 25;

/**
 * Parse textarea or comma-separated input into a deduped list of non-empty strings.
 */
export function parseAliasInput(text) {
  if (!text || typeof text !== 'string') return [];
  const parts = text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return [...new Set(parts)].slice(0, MAX_ALIASES);
}

/**
 * Format aliases for display in a textarea (one per line).
 */
export function formatAliasesForInput(aliases) {
  if (!Array.isArray(aliases) || aliases.length === 0) return '';
  return aliases.filter(Boolean).join('\n');
}

/**
 * @returns {Promise<{ success: boolean, map?: Record<string, string[]>, error?: string }>}
 */
export async function fetchPaymentSearchAliasesByClub(clubId) {
  if (!clubId) return { success: true, map: {} };
  const { data, error } = await supabase
    .from('club_member_payment_search_aliases')
    .select('player_id, aliases')
    .eq('club_id', clubId);

  if (error) {
    console.error('[fetchPaymentSearchAliasesByClub]', error);
    return { success: false, error: error.message, map: {} };
  }
  const map = {};
  (data || []).forEach((row) => {
    map[row.player_id] = Array.isArray(row.aliases) ? row.aliases : [];
  });
  return { success: true, map };
}

export function mergeAliasesIntoPlayers(players, aliasMap) {
  if (!Array.isArray(players)) return [];
  return players.map((p) => ({
    ...p,
    payment_search_aliases: aliasMap[p.id] || [],
  }));
}

/**
 * Match display name, email, or host-only aliases (case-insensitive substring).
 */
export function playerMatchesPaymentSearch(player, searchRaw) {
  const q = (searchRaw || '').trim().toLowerCase();
  if (!q) return true;
  const firstName = (player.first_name || '').trim().toLowerCase();
  const lastName = (player.last_name || '').trim().toLowerCase();
  const full = `${firstName} ${lastName}`.trim();
  if (full.includes(q)) return true;
  const legacyName = (player.name || '').trim().toLowerCase();
  if (legacyName.includes(q)) return true;
  const email = (player.email || '').toLowerCase();
  if (email && email.includes(q)) return true;
  const aliases = player.payment_search_aliases;
  if (Array.isArray(aliases)) {
    for (const a of aliases) {
      if (a && String(a).toLowerCase().includes(q)) return true;
    }
  }
  return false;
}

/**
 * Save or clear aliases for one member. Empty list deletes the row.
 */
export async function savePaymentSearchAliases(clubId, playerId, aliasLinesText) {
  const aliases = parseAliasInput(aliasLinesText);
  if (aliases.length === 0) {
    const { error } = await supabase
      .from('club_member_payment_search_aliases')
      .delete()
      .eq('club_id', clubId)
      .eq('player_id', playerId);
    if (error) {
      console.error('[savePaymentSearchAliases] delete', error);
      return { success: false, error: error.message };
    }
    return { success: true, aliases: [] };
  }

  const { error } = await supabase.from('club_member_payment_search_aliases').upsert(
    {
      club_id: clubId,
      player_id: playerId,
      aliases,
    },
    { onConflict: 'club_id,player_id' }
  );

  if (error) {
    console.error('[savePaymentSearchAliases] upsert', error);
    return { success: false, error: error.message };
  }
  return { success: true, aliases };
}
